import { memo, useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { History as HistoryIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useDashboardBuilder, useDashboardMutation } from '@trinity/chat-components/containers/Dashboard/context';
import chatBackgroundImg from '@trinity/chat-components/static/images/chat-background.png';
import { BuilderCanvas } from './BuilderCanvas';

type Props = {
  /** Dashboard name displayed as the page header */
  title?: string;
  /** Called when the user clicks Version History */
  onShowVersionHistory?: () => void;
};

/**
 * BuilderToolbar  the top header of the dashboard builder.
 *
 * Contains: dashboard title, Export JSON button, and Save button.
 */
export const BuilderToolbar = memo(function BuilderToolbar({
  title,
  onShowVersionHistory,
}: Props): React.ReactElement {
  const { isDirty } = useDashboardBuilder();
  const { onSave, onCancel } = useDashboardMutation();

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDescription, setSaveDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave(saveDescription.trim() || undefined);
      setSaveDialogOpen(false);
      setSaveDescription('');
    } catch {
      // Error feedback is the responsibility of the onSave caller (e.g. PageBuilder shows a toast).
      // We catch here to prevent unhandled rejection and ensure saving state resets.
    } finally {
      setSaving(false);
    }
  }, [onSave, saveDescription]);

  return (
    <>
      <ToolbarRoot>
        <Typography variant="h5" fontWeight={700} component="h1">
          {title ?? 'Dashboard'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Preview as homepage">
            <IconButton size="small" onClick={() => setPreviewOpen(true)} aria-label="Preview as homepage">
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          {onShowVersionHistory && (
            <Tooltip title="Version history">
              <IconButton size="small" onClick={onShowVersionHistory} aria-label="Version history">
                <HistoryIcon />
              </IconButton>
            </Tooltip>
          )}
          {isDirty ? (
            <Button
              variant="contained"
              size="small"
              disabled={saving}
              onClick={() => setSaveDialogOpen(true)}
            >
              Save
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
        </Box>
      </ToolbarRoot>

      {/* Save dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Save Dashboard</DialogTitle>
        <DialogContent>
          <TextField
            label="Version description (optional)"
            value={saveDescription}
            onChange={e => setSaveDescription(e.target.value)}
            fullWidth
            autoFocus
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Homepage preview dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {/* Floating close button — no toolbar chrome so the preview reads like a real dashboard */}
        <Box sx={{ position: 'absolute', top: 12, right: 16, zIndex: 1600 }}>
          <Button variant="outlined" size="small" onClick={() => setPreviewOpen(false)}>
            Close Preview
          </Button>
        </Box>
        <PreviewBody>
          <BuilderCanvas readOnly title={title} />
        </PreviewBody>
      </Dialog>
    </>
  );
});

const ToolbarRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  minHeight: 64,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
}));

const PreviewBody = styled('div')({
  flex: 1,
  overflow: 'auto',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  background: `url(${chatBackgroundImg}) no-repeat`,
  backgroundSize: 'cover',
  // Make BuilderCanvas transparent so the chat background shows through
  '& > div': {
    backgroundColor: 'transparent !important',
    width: '85%',
    maxWidth: '85%',
    margin: '0 auto',
  },
});
